<template>
    <div id="price-periods-section" class="periods" v-if="hasFieldToDisplay()">
        <div class="tabulation-title">
            <h5>
                <v-icon class="blue--text text--darken-2">label</v-icon> Tarifs
            </h5>
        </div>

      <div class="form-container mr-auto ml-auto mt-5" v-if="hasField('IndicationTarif') && getFamilyByCode('IndicationTarif').length > 0">
        <v-autocomplete
            :value="pricingIndication"
            :items="getFamilyByCode('IndicationTarif')"
            chips
            @change="updatePricingIndication($event, 'pricingIndication')"
            item-text="label"
            label="Indication tarif"
            return-object
            outlined>
        </v-autocomplete>
      </div>

        <div class="mr-auto ml-auto mt-3 mb-5 element tab-periods-container"
            v-for="(period, i) in provider.price_periods" v-if="period.availableElements.length">

            <!-- Tab title -->
            <div class="tab-periods-title-container mb-10">
                <div class="d-flex align-items-center tab-periods-title">
                    <h6 class="mb-0">
                        Du <b>{{ formatDate(period.valid_from, false) }}</b>
                        au <b>{{ formatDate(period.valid_through, false)}}</b>
                    </h6>
                    <v-icon @click="openEditModal(period)">edit</v-icon>
                </div>
                <div>
                    <div class="currency-selector">
                        <v-autocomplete :items="currencies" v-model="period.currency" label="Devise" item-text="label"
                            item-value="code" return-object outlined>
                        </v-autocomplete>
                    </div>
                </div>
                <div>
                    <v-tooltip slot="append-outer" bottom>
                        <template v-slot:activator="{ on }">
                            <v-icon v-on="on" color="red" @click="confirmDelete(i)">delete</v-icon>
                        </template>
                        <span>Supprimer la période tarifaire</span>
                    </v-tooltip>
                </div>
            </div>

            <!-- Tab content -->
            <div class="tab-periods-content p-3 mb-5" v-for="(price, index) in period.prices" :key="price.element.id"
                v-if="hasReferentialElement(price.element)">

                <div class="d-flex justify-content-between align-items-center mb-2">
                    <b>{{ price.element.label }}</b>
                    <v-btn icon class="delete-price" @click="deletePrice(i, index)">
                        <v-icon size="22px" class="red--text">clear</v-icon>
                    </v-btn>
                </div>

                <div class="row align-items-start mb-3">
                    <div class="col-4">
                        <v-autocomplete class="price-mode-input mb-2" :items="priceModes" v-model="price.mode"
                            label="Mode" item-text="label" item-value="code" outlined>
                        </v-autocomplete>
                    </div>
                    <div class="col-4 d-flex align-items-center">
                        <v-text-field v-model="price.min" outlined type="number"></v-text-field>
                        <p class="mb-0 ml-2">{{ period.currency.label }}</p>
                    </div>
                    <div class="col-4 d-flex align-items-center" v-if="price.mode === 'MIN_MAX'">
                        <v-text-field v-model="price.max" outlined type="number"></v-text-field>
                        <p class="mb-0 ml-2">{{ period.currency.label }}</p>
                    </div>
                </div>

                <v-textarea class="textarea" v-model="price.detail" hide-details height="100px" label="Précisions"
                    outlined>
                </v-textarea>
            </div>

            <!-- Tab content add -->
            <div class="complement mt-10">
                <v-autocomplete class="price-mode-input" :items="period.availableElements" label="Type de tarif"
                    item-text="label" v-model="period.selectedElements" prepend-icon="add" multiple
                    @change="addOrDeleteElements($event, period)" return-object outlined>
                </v-autocomplete>
            </div>
        </div>

        <v-alert type="error" text
            v-if="provider.price_periods.length === 0 && !disabled && hasFamilyElement('OfferType')"
            class="mt-5 mb-5 form-container mr-auto ml-auto" border="right" colored-border>
            <p>
                Vous n'avez pas renseigné vos tarifs. Il s'agit d'une information importante pour vos visiteurs.
            </p>
            <p>
                Nous vous suggérons de prendre quelques minutes pour bien renseigner vos tarifs.
            </p>
        </v-alert>

        <div class="text-center mt-4" v-if="hasFamilyElement('OfferType')">
            <v-btn color="primary" class="add-period-button" @click="clickAddPeriod">
                <v-icon small class="mr-2">add</v-icon>
                Ajouter une période tarifaire
            </v-btn>
        </div>

        <div class="form-container mr-auto ml-auto text-center mt-5" v-if="hasField('COMPLEMENT_TARRIFAIRE')">
            <v-textarea class="textarea" v-model="provider.pricing_complement" label="Complément tarifaire" outlined>
            </v-textarea>
        </div>

        <div class="form-container mr-auto ml-auto mt-5 mb-5" v-for="(link, index) in provider.booking_links"
            v-if="hasField('BOOKING_LINK') && index === 0">

            <v-text-field class="textarea" label="Lien de réservation" v-model="link.value" hide-details outlined>
                <template v-slot:append>
                    <v-tooltip bottom v-if="link.value !== null && link.value !== '' && checkFormat(link)">
                        <template v-slot:activator="{ on }">
                            <a :href="link.value" target="_blank" v-on="on">
                                <v-icon color="primary">open_in_new</v-icon>
                            </a>
                        </template>
                        <div>Ouvrir dans un nouvel onglet</div>
                    </v-tooltip>

                    <v-tooltip bottom v-else-if="!checkFormat(link)">
                        <template v-slot:activator="{ on }">
                            <a v-on="on">
                                <v-icon color="warning">info</v-icon>
                            </a>
                        </template>
                        <div>Le lien de réservation n'est pas au bon format</div>
                    </v-tooltip>
                </template>
            </v-text-field>
        </div>

        <v-dialog ref="dialog" v-model="showEditPeriodModal" persistent width="290px">
            <v-card>
                <v-card-title>
                    <span class="headline">Modifier une période</span>
                </v-card-title>
                <v-card-text>
                    <v-text-field v-model="dateStartFr" label="Date de début" readonly @click="openPicker('dateStart')"
                        outlined>
                    </v-text-field>
                    <v-text-field v-model="dateStopFr" label="Date de fin" readonly @click="openPicker('dateStop')"
                        outlined>
                    </v-text-field>

                    <v-alert type="error" v-if="message !== null">
                        {{ message }}
                    </v-alert>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="error" @click="showEditPeriodModal = false">Annuler</v-btn>
                    <v-btn color="primary" :disabled="message !== null ||
                    dateStop === null ||
                    dateStart == null" @click="updatePeriod">
                        Modifier
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog ref="dialog" v-model="showPicker" persistent width="290px">
            <v-date-picker locale="fr-FR" color="primary" v-model="selectedDate" @input="setDatePicker"
                :first-day-of-week="1">
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="showPicker = false">Annuler</v-btn>
                <v-btn text color="primary" @click="showPicker = false">Valider</v-btn>
            </v-date-picker>
        </v-dialog>

        <confirm ref="confirm"></confirm>

        <v-dialog ref="dialog" v-model="showAddPeriodModal" persistent width="350px">
            <v-card>
                <v-card-title>
                    <span class="headline">Vous êtes ouvert</span>
                </v-card-title>
                <v-card-text>
                    <v-text-field v-model="dateStartFr" label="Du" readonly @click="openPicker('dateStart')" outlined>
                    </v-text-field>
                    <v-text-field v-model="dateStopFr" label="Au" readonly @click="openPicker('dateStop')" outlined>
                    </v-text-field>

                    <v-alert type="error" v-if="message !== null">
                        {{ message }}
                    </v-alert>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="error" @click="showAddPeriodModal = false">Fermer</v-btn>
                    <v-btn color="primary" :disabled="message !== null ||
                    dateStop === null ||
                    dateStart == null" @click="saveDate">
                        Ajouter
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex"
import Confirm from "@/components/tools/Confirm"

export default {
    name: "PricePeriods",
    components: { Confirm },
    data() {
        return {
            showEditPeriodModal: false,
            selectedPeriod: null,
            showAddPeriodModal: false,
            dateStart: null,
            dateStop: null,
            dateStartFr: null,
            dateStopFr: null,
            message: null,
            selectedDateProperty: null,
            showPicker: false,
            selectedDate: null,
            priceModes: [
                { code: 'MIN_MAX', label: 'Min / max' },
                { code: 'UNIQUE', label: 'Tarif unique' },
                { code: 'FROM', label: 'À partir de' }
            ],
            pricingIndication: [],
        }
    },
    methods: {
        hasFieldToDisplay() {
            return this.hasFamilyElement('OfferType') ||
                this.hasField('COMPLEMENT_TARRIFAIRE') ||
                this.hasField('BOOKING_LINK')
        },
        openEditModal(period) {
            this.showEditPeriodModal = true
            this.selectedPeriod = period
            this.dateStart = this.selectedPeriod.valid_from
            this.dateStop = this.selectedPeriod.valid_through
            this.dateStartFr = this.formatDate(this.selectedPeriod.valid_from, false)
            this.dateStopFr = this.formatDate(this.selectedPeriod.valid_through, false)
        },
        confirmDelete(index) {
            let period = this.provider.price_periods[index]

            this.$refs.confirm.open('Attention',
                'Vous allez supprimer les tarifs saisies pour la période du ' + this.formatDate(period.valid_from, false) +
                ' au ' + this.formatDate(period.valid_through, false) + '. ' +
                'Êtes-vous sûr de vouloir supprimer cette période ?',
                { color: 'red' }).
                then((confirm) => {
                    if (confirm) {
                        let index = this.provider.price_periods.findIndex((_period) => _period === period)
                        this.deletePeriod(index)
                    }
                })
        },
        deletePeriod(index) {
            this.provider.price_periods.splice(index, 1)

            this.$forceUpdate()
        },
        openPicker(property) {
            this.selectedDateProperty = property
            this.showPicker = true
        },
        updatePeriod() {
            this.selectedPeriod.valid_from = this.dateStart
            this.selectedPeriod.valid_through = this.dateStop

            this.showEditPeriodModal = false
        },
        setDatePicker(value) {
            this[this.selectedDateProperty] = value
            this[this.selectedDateProperty + 'Fr'] = this.formatDate(value, false)

            this.message = this.checkDateValidity()
        },
        checkDateValidity() {
            let message = null

            if (this.dateStart > this.dateStop) {
                message = "La date de début doit précéder la date de fin"
            }

            this.provider.price_periods.forEach(period => {
                if (period !== this.selectedPeriod &&
                    ((this.dateStart >= period.valid_from && this.dateStart <= period.valid_through) ||
                        (this.dateStop >= period.valid_from && this.dateStop <= period.valid_through))) {
                    message = "La période que vous créez actuellement recouvre une autre période"
                }
            })

            return message
        },
        deletePrice(periodIndex, priceIndex) {
            this.provider.price_periods[periodIndex].selectedElements.splice(priceIndex, 1)

            this.provider.price_periods[periodIndex].prices.splice(priceIndex, 1)
        },
        addOrDeleteElements(event, period) {
            if (event.length > period.prices.length) {
                this.addElements(event, period)
            } else {
                this.removeElement(event, period)
            }
        },
        addElements(event, period) {
            event.forEach(_e => {
                let exist = period.prices.filter(price => price.element.id === _e.id).length !== 0

                if (!exist) {
                    let obj = {
                        element: _e,
                        min: null,
                        max: null,
                        detail: null,
                        mode: 'MIN_MAX'
                    }

                    period.prices.push(obj)
                }
            })
        },
        removeElement(event, period) {
            period.prices.forEach((price, index) => {
                let exist = event.filter(_price => price.element.id === _price.id).length !== 0

                if (!exist) {
                    period.prices.splice(index, 1)
                }
            })
        },
        clickAddPeriod() {
            this.selectedPeriod = null
            this.showAddPeriodModal = true

            this.dateStartFr = null
            this.dateStopFr = null
        },
        saveDate() {
            this.addPeriod()

            this.showAddPeriodModal = false
        },
        addPeriod() {
            let period = {
                prices: [],
                valid_from: null,
                valid_through: null,
                currency: this.currencies[0],
                availableElements: this.getFamilyByCode('OfferType'),
                selectedElements: []
            }

            period.valid_from = this.dateStart
            period.valid_through = this.dateStop

            this.provider.price_periods.push(period)
        },
        initPriceSelections() {
            this.provider.price_periods.forEach(period => {
                let prices = period.prices.map(price => price.element)

                this.$set(period, 'selectedElements', prices)
                this.$set(period, 'availableElements', this.getFamilyByCode('OfferType'))
            })
        },
        hasReferentialElement(element) {
            let familyElement = this.familyElements.find(family => family.name === 'OfferType')

            let has = false

            if (familyElement !== null) {
                has = familyElement.elements.find(_element => _element.id === element.id)
            }

            return has
        },
        initBookingLinks() {
            if (this.provider.booking_links.length === 0) {
                this.provider.booking_links.push({
                    type: {
                        name: 'WEBSITE'
                    },
                    value: null
                })
            }
        },
        checkFormat(link) {
            if (link.value === '' || link.value === null) {
                return true
            }

            let url

            try {
                url = new URL(link.value)
            } catch (_) {
                return false
            }

            return url.protocol === "http:" || url.protocol === "https:"
        },
      updatePricingIndication(newElements, property) {
        this.provider.elements.push(newElements)
        this[property] = newElements
      },
      deleteChip(itemNeedToRemove, property) {
        this.provider.elements.forEach((el, index) => {
          if(el.id === itemNeedToRemove.id) {
            this.provider.elements.splice(index, 1)
          }
        })
        this[property] = []
      },
    },
    computed: {
        ...mapState('web_form', ['provider', 'disabled', 'currencies', 'familyElements']),
        ...mapGetters('web_form', ['hasField', 'hasFamilyElement', 'getFamilyByCode', 'getElementsByFamilyCode'])
    },
    created() {
        this.initPriceSelections()
        this.initBookingLinks()

        this.pricingIndication = this.getElementsByFamilyCode('IndicationTarif')[0]
    }
}
</script>